import * as React from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  Icon,
  List as MuiList,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';

import { Button, Spinner, Tooltip } from 'ui';
import palette from 'ui/theme/palette';

export type ListOption = {
  archivedOption?: boolean;
  children?: ListOption[];
  disabled?: boolean;
  startIcon?: JSX.Element;
  icon?: JSX.Element;
  label: string;
  parentValue?: string;
  selected?: boolean;
  sublabel?: React.ReactNode;
  value: string;
};

type OptionMap = {
  [key: string]: OptionMapItem;
};

type OptionMapItem = {
  archived?: boolean;
  checked: boolean;
  children?: OptionMap;
  disabled?: boolean;
  startIcon?: JSX.Element;
  icon?: JSX.Element;
  label: string;
  open: boolean;
  sublabel?: React.ReactNode;
};

/** Specified by the designs. */
const sublabelTextStyle = {
  color: palette.text.secondary,
  fontFamily: 'Lato',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '1.4rem'
};

interface Props {
  options: ListOption[];
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  onSelect?: (value: string, selected: boolean) => void;
  onUserClickCallback?: (value: string, selected: boolean) => void;
  indentItems?: boolean;
  disabledItemTooltip?: string;
  dataCy?: string;
  archiveOptionIcon?: React.ReactNode;
  showArchived?: boolean;
  onClearChildren?: (parentId: string) => void;
}

export const List: React.FC<Props> = (props) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [optionMap, setOptionMap] = React.useState<OptionMap>({});

  React.useEffect(() => {
    const buildOptionsMap = (options: ListOption[]) => {
      const newOptionMap: OptionMap = {};

      options.forEach((opt) => {
        newOptionMap[opt.value] = {
          archived: opt.archivedOption,
          checked: !!opt.selected,
          children: opt.children ? buildOptionsMap(opt.children) : undefined,
          disabled: opt.disabled,
          startIcon: opt.startIcon,
          icon: opt.icon,
          label: opt.label,
          open: true,
          sublabel: opt.sublabel
        };
      });

      return newOptionMap;
    };

    setOptionMap(buildOptionsMap(props.options));
    setIsLoading(false);
  }, [props.options]);

  const getOption = (options: OptionMap, id: string) => {
    if (options[id]) return options[id];
    for (const opt of Object.values(options)) {
      if (opt.children?.[id]) {
        return opt.children[id];
      }
    }
  };

  const countSelectedChildren = (rootId: string) => {
    const root = optionMap[rootId];

    if (!root || !root.children) {
      return { total: 0, count: 0 };
    }

    let count = 0;
    let total = 0;

    Object.values(root.children).forEach((child) => {
      total += 1;

      if (child.checked) {
        count += 1;
      }
    });

    return { count, total };
  };

  const handleToggleChecked = (value: string) => {
    const newOptionMap = { ...optionMap };

    const option = getOption(newOptionMap, value);
    if (option) {
      option.checked = !option.checked;
      props.onSelect?.(value, option.checked);
      if (option.children) {
        Object.entries(option.children).forEach(([id, childOption]) => {
          childOption.checked = option.checked;
          props.onSelect?.(id, option.checked);
        });
      }
    }
    setOptionMap(newOptionMap);
  };

  const handleToggleOpen = (value: string) => {
    const newOptions = { ...optionMap };
    newOptions[value].open = !newOptions[value].open;

    setOptionMap(newOptions);
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <MuiList
      disablePadding
      data-cy={props.dataCy}
      sx={{
        height: props.height ? props.height : 'auto',
        minHeight: props.minHeight,
        maxHeight: props.maxHeight,
        overflow: 'auto'
      }}
    >
      {Object.keys(optionMap).map((key) => {
        const labelId = `list-label-${key}`;
        const option = optionMap[key];
        const children: OptionMap | undefined = option.children;
        const hasChildren = children && Object.keys(children).length > 0;
        const selectedChildren = countSelectedChildren(key);
        const showToolTip = option.disabled === true && props.disabledItemTooltip != null;

        return (
          <Box key={key}>
            <ListItem disablePadding>
              <ListItemButton
                dense
                disableRipple
                disableGutters
                data-cy={labelId}
                role={undefined}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (!option.disabled) {
                    handleToggleChecked(key);

                    if (props.onUserClickCallback) {
                      props.onUserClickCallback(key, option.checked);
                    }
                  }
                }}
                selected={option.checked}
                sx={{ p: props.indentItems ? '0 0 0 53px' : '0' }}
              >
                {hasChildren && (
                  <ListItemIcon
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      handleToggleOpen(key);
                    }}
                    sx={{ mr: 0 }}
                  >
                    {option.open ? <Icon>expand_more</Icon> : <Icon>keyboard_arrow_right</Icon>}
                  </ListItemIcon>
                )}

                <ListItemIcon
                  sx={{
                    alignSelf: option.sublabel ? 'flex-start' : 'center',
                    ml: hasChildren ? '0' : props.indentItems ? '12px' : '36px',
                    mr: 0,
                    '&: hover': {
                      cursor: !!option.disabled ? 'not-allowed' : 'pointer'
                    },
                    minWidth: '24px'
                  }}
                >
                  {
                    <Tooltip
                      disabled={!showToolTip}
                      placement="top-start"
                      title={props.disabledItemTooltip as string}
                    >
                      <Checkbox
                        disableRipple
                        disabled={option.disabled}
                        checked={
                          option.checked ||
                          (selectedChildren.count > 0 && selectedChildren.count >= selectedChildren.total)
                        }
                        edge="start"
                        indeterminate={
                          selectedChildren.count > 0 && selectedChildren.count < selectedChildren.total
                        }
                        inputProps={{ 'aria-labelledby': labelId }}
                        size="small"
                        sx={{
                          color: (theme) => theme.palette.divider,
                          border: (theme) => `1px solid ${theme.palette.common.white}`,
                          '&.Mui-focusVisible': {
                            border: (theme) => `1px solid ${theme.palette.primary.main}`,
                            boxSizing: 'border-box',
                            backgroundColor: 'transparent',
                            borderRadius: '4px'
                          }
                        }}
                        tabIndex={-1}
                      />
                    </Tooltip>
                  }
                </ListItemIcon>

                <ListItemText
                  disableTypography
                  id={labelId}
                  primary={
                    <div style={{ margin: option.sublabel ? '10px 0 0 0' : '0' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {option.startIcon}
                        <span style={{ marginRight: '8px' }}>{option.label}</span>
                        {option.archived && props.archiveOptionIcon ? props.archiveOptionIcon : null}
                        {option.icon}
                      </div>
                    </div>
                  }
                  secondary={
                    option.sublabel ? <div style={{ ...sublabelTextStyle }}>{option.sublabel}</div> : null
                  }
                  sx={{
                    margin: '0px',
                    color: option.disabled ? '#A5A8BA' : '#3a3c45',
                    fontFamily: 'Lato',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    '&: hover': {
                      cursor: !!option.disabled ? 'not-allowed' : 'pointer'
                    }
                  }}
                />

                {props.onClearChildren && selectedChildren.count > 0 && (
                  <Button
                    variant={'text'}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      props.onClearChildren && props.onClearChildren(key);
                    }}
                    label={'Clear'}
                    color={'primary-text'}
                    style={{ padding: '0', height: 'min-content' }}
                  />
                )}
              </ListItemButton>
            </ListItem>

            {hasChildren && (
              <Collapse in={option.open} timeout="auto">
                <List
                  indentItems
                  archiveOptionIcon={props.archiveOptionIcon}
                  showArchived={props.showArchived}
                  onSelect={handleToggleChecked}
                  onUserClickCallback={props.onUserClickCallback}
                  options={Object.entries(children).map(([id, child]) => {
                    return {
                      archivedOption: child.archived,
                      disabled: child.disabled,
                      startIcon: child.startIcon,
                      icon: child.icon,
                      label: child.label,
                      selected: child.checked,
                      value: id
                    };
                  })}
                />
              </Collapse>
            )}
          </Box>
        );
      })}
    </MuiList>
  );
};
